import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/Leftovers/Add";
import { TableData } from "@/App/Leftovers/Table";
import { getLeftoverProductsProcess } from "@/Api/leftoverProducts";
import { getSelectProductGroups } from "@/Api/productGroups";
import { LeftoverProductProcess } from "@/types/leftoverProducts";
import { useAuth } from "@/hooks/auth";
import { Package, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { Mask } from "@/utils/mask";

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<LeftoverProductProcess>);
  const [state, setState] = useState("loading");
  const [totalLeftoverValue, setTotalLeftoverValue] = useState<number>(0);
  const { user } = useAuth();
  const unityId = user?.unity?.[0]?.id;

  // States para filtros
  const [lotteries, setLotteries] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedLotteries, setSelectedLotteries] = useState<string[]>([]);
  const [validationStartDate, setValidationStartDate] = useState<string>("");
  const [validationEndDate, setValidationEndDate] = useState<string>("");


  const fetchData = async (
    page: number = 1,
    lotteryIds: string[] = [],
    valStartDate?: string,
    valEndDate?: string
  ) => {
    setState("loading");
    try {
      const { data } = await getLeftoverProductsProcess(page, {
        unity_id: unityId?.toString(),
        lottery_id: lotteryIds.length > 0 ? lotteryIds : undefined,
        validation_start_date: valStartDate || undefined,
        validation_end_date: valEndDate || undefined,
      });
      const response = data?.response;
      // Extract summary and set total value
      if (response?.summary?.total_leftover_value !== undefined) {
        setTotalLeftoverValue(response.summary.total_leftover_value);
      }
      setListData(response);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setState("production");
    }
  };

  const fetchLotteries = async () => {
    try {
      const { data, ok } = await getSelectProductGroups();
      if (ok && data?.response) {
        setLotteries(data.response);
      }
    } catch (error) {
      console.error("Error fetching lotteries:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchLotteries();
  }, [unityId]);

  const handleUpdateFront = (updateData: any) => {
    if (updateData?.shouldRefresh) {
      fetchData(1, selectedLotteries, validationStartDate, validationEndDate);
    }
  };

  const handleApplyFilter = () => {
    fetchData(1, selectedLotteries, validationStartDate, validationEndDate);
  };

  const handleClearFilter = () => {
    setSelectedLotteries([]);
    setValidationStartDate("");
    setValidationEndDate("");
    fetchData(1, [], "", "");
  };

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(
          listData?.current_page + 1,
          selectedLotteries,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(
          listData?.current_page - 1,
          selectedLotteries,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(
          listData?.last_page,
          selectedLotteries,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
  };

  return (
    <Main>
      <div className="space-y-4">
        <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
          <h2 className="text-2xl font-semibold flex gap-2 items-center">
            <Package className="h-6 w-6" />
            Sobras
          </h2>
          <AddBtn updateFront={handleUpdateFront} />
        </div>

        {/* Filtros e Total */}
        <div className="bg-white dark:bg-[#2c2c2c] rounded-lg border overflow-hidden">
          <div className="flex flex-col md:flex-row">
            {/* Total em Sobras - Esquerda */}
            <div className="bg-gradient-to-r from-green-400 to-emerald-500 text-white p-6 pr-10 md:w-72 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <DollarSign className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white/80">Valor Total em Sobras</p>
                  <p className="text-2xl font-bold">{Mask.formatCurrency(totalLeftoverValue)}</p>
                </div>
              </div>
            </div>

            {/* Filtros - Direita */}
            <div className="p-4 flex-1">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="validation_start_date">Data Validação Início</Label>
                  <Input
                    id="validation_start_date"
                    type="date"
                    value={validationStartDate}
                    onChange={(e) => setValidationStartDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="validation_end_date">Data Validação Fim</Label>
                  <Input
                    id="validation_end_date"
                    type="date"
                    value={validationEndDate}
                    onChange={(e) => setValidationEndDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lottery-filter">Loteria</Label>
                  <MultiSelectCheckbox
                    options={lotteries.map((lottery) => ({
                      value: lottery.id.toString(),
                      label: lottery.name,
                    }))}
                    selectedValues={selectedLotteries}
                    onSelectionChange={setSelectedLotteries}
                    placeholder="Selecione a loteria"
                    selectAllLabel="SELECIONAR TUDO"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleApplyFilter}
                    className={cn(colors.bg_primary, "flex-1")}
                  >
                    Filtrar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClearFilter}
                  >
                    Limpar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="flex flex-col gap-3">
          {state === "loading" ? (
            <CreateLoaderTable cols={9} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
            />
          )}
        </div>
      </div>
    </Main>
  );
};
